import React, { useState, useEffect } from 'react';
import { X, Eye, Send, CheckCircle, Loader2 } from 'lucide-react';
import { getTestResultsByTestId, getUserProfile, saveStudentDocument } from '../../firebase/collections';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import toast from 'react-hot-toast';

const TestEvaluationModal = ({ isOpen, onClose, test }) => {
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'evaluated'
  const [testResponses, setTestResponses] = useState([]); // Pending responses
  const [evaluatedResponses, setEvaluatedResponses] = useState([]); // Evaluated responses
  const [evaluationScores, setEvaluationScores] = useState({}); // Store scores being entered
  const [savingEvaluation, setSavingEvaluation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userDataCache, setUserDataCache] = useState({}); // Cache user data by userId
  const [sendingResults, setSendingResults] = useState({}); // Track which results are being sent
  const [sentResults, setSentResults] = useState(new Set()); // Track which results have been sent

  // Load responses when modal opens
  useEffect(() => {
    if (isOpen && test) {
      loadResponses();
    } else {
      // Reset state when modal closes
      setTestResponses([]);
      setEvaluatedResponses([]);
      setEvaluationScores({});
      setActiveTab('pending');
      setSentResults(new Set());
      setSendingResults({});
    }
  }, [isOpen, test]);

  const loadResponses = async () => {
    if (!test) return;
    
    setLoading(true);
    try {
      const responsesResult = await getTestResultsByTestId(test.id);
      if (responsesResult.success) {
        const allResponses = responsesResult.data.filter(r => r.manualEvaluation);
        
        // Fetch user data for responses missing userName/userEmail
        const responsesToEnrich = allResponses.filter(r => !r.userName || !r.userEmail);
        const userIdsToFetch = [...new Set(responsesToEnrich.map(r => r.userId).filter(Boolean))];
        
        // Fetch user data in parallel
        const userDataPromises = userIdsToFetch.map(async (userId) => {
          if (userDataCache[userId]) {
            return { userId, data: userDataCache[userId] };
          }
          try {
            const userResult = await getUserProfile(userId);
            if (userResult.success && userResult.data) {
              const userData = {
                name: userResult.data.name || userResult.data.displayName || '',
                email: userResult.data.email || ''
              };
              setUserDataCache(prev => ({ ...prev, [userId]: userData }));
              return { userId, data: userData };
            }
          } catch (error) {
            console.error(`Error fetching user ${userId}:`, error);
          }
          return { userId, data: null };
        });
        
        const userDataResults = await Promise.all(userDataPromises);
        const userDataMap = {};
        userDataResults.forEach(({ userId, data }) => {
          if (data) userDataMap[userId] = data;
        });
        
        // Enrich responses with user data
        const enrichedResponses = allResponses.map(response => {
          if (!response.userName || !response.userEmail) {
            const userData = userDataMap[response.userId] || userDataCache[response.userId];
            if (userData) {
              return {
                ...response,
                userName: response.userName || userData.name || 'Unknown',
                userEmail: response.userEmail || userData.email || ''
              };
            }
          }
          return response;
        });
        
        setTestResponses(enrichedResponses.filter(r => r.status === 'pending_evaluation'));
        setEvaluatedResponses(
          enrichedResponses
            .filter(r => r.status === 'evaluated')
            .sort((a, b) => (b.score || 0) - (a.score || 0))
        );
      }
    } catch (error) {
      console.error('Error loading responses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEvaluation = async (response) => {
    setSavingEvaluation(true);
    try {
      // Calculate total score
      const totalScore = (response.questionsWithAnswers || []).reduce((sum, q, qIndex) => {
        const questionId = q.id || `q${qIndex}`;
        const scoreKey = `${response.id}_${questionId}`;
        return sum + (evaluationScores[scoreKey] || 0);
      }, 0);
      
      // Get max possible score
      const maxScore = (response.questionsWithAnswers || []).reduce((sum, q) => {
        return sum + (q.markingScheme?.correct || 4);
      }, 0);
      
      const percentageScore = maxScore > 0 ? (totalScore / maxScore) * 100 : 0;
      
      // Update test result
      const resultRef = doc(db, COLLECTIONS.TEST_RESULTS, response.id);
      await updateDoc(resultRef, {
        status: 'evaluated',
        score: totalScore,
        totalMarks: maxScore,
        percentageScore: percentageScore,
        evaluatedAt: new Date().toISOString(),
        questionScores: (response.questionsWithAnswers || []).reduce((scores, q, qIndex) => {
          const questionId = q.id || `q${qIndex}`;
          const scoreKey = `${response.id}_${questionId}`;
          scores[questionId] = evaluationScores[scoreKey] || 0;
          return scores;
        }, {})
      });
      
      // Remove from pending list and add to evaluated
      const updatedResponse = {
        ...response,
        status: 'evaluated',
        score: totalScore,
        totalMarks: maxScore,
        percentageScore: percentageScore,
        evaluatedAt: new Date().toISOString(),
        questionScores: (response.questionsWithAnswers || []).reduce((scores, q, qIndex) => {
          const questionId = q.id || `q${qIndex}`;
          const scoreKey = `${response.id}_${questionId}`;
          scores[questionId] = evaluationScores[scoreKey] || 0;
          return scores;
        }, {})
      };
      
      setTestResponses(prev => prev.filter(r => r.id !== response.id));
      setEvaluatedResponses(prev => {
        const updated = [...prev, updatedResponse].sort((a, b) => (b.score || 0) - (a.score || 0));
        return updated;
      });
      
      // Clear scores for this response
      const scoresToRemove = {};
      (response.questionsWithAnswers || []).forEach((q, qIndex) => {
        const questionId = q.id || `q${qIndex}`;
        const scoreKey = `${response.id}_${questionId}`;
        scoresToRemove[scoreKey] = undefined;
      });
      setEvaluationScores(prev => {
        const newScores = { ...prev };
        Object.keys(scoresToRemove).forEach(key => delete newScores[key]);
        return newScores;
      });
      
      // Switch to evaluated tab after saving
      setActiveTab('evaluated');
    } catch (error) {
      console.error('Failed to save evaluation:', error);
      alert(`Failed to save evaluation: ${error.message}`);
    } finally {
      setSavingEvaluation(false);
    }
  };

  const handleSendToBatchSchedule = async (response) => {
    if (!response.userEmail) {
      toast.error('Student email is required to send results');
      return;
    }

    setSendingResults(prev => ({ ...prev, [response.id]: true }));

    try {
      // Get test date (use startTime if available, otherwise use completedAt or current date)
      const testDate = test.startTime || response.completedAt || response.createdAt || new Date().toISOString();
      
      // Prepare result data with all fields
      // Normalize email to ensure it matches query
      const normalizedEmail = response.userEmail ? response.userEmail.toLowerCase().trim() : '';
      
      // Debug: Log the data being saved
      console.log('Sending result to batch schedule:', {
        studentEmail: normalizedEmail,
        studentName: response.userName || response.userEmail,
        examName: test.title,
        score: response.score,
        percentageScore: response.percentageScore
      });
      
      const resultData = {
        studentEmail: normalizedEmail,
        studentId: response.userId || null,
        studentName: response.userName || response.userEmail || 'Unknown',
        documentType: 'result',
        documentUrl: '', // Can be empty or add PDF generation later
        examName: test.title || 'Test',
        examDate: testDate,
        batchId: test.batchId || null,
        coachingId: test.coachingId || null,
        // Add all test result fields
        testId: test.id,
        testResultId: response.id,
        score: response.score || 0,
        totalMarks: response.totalMarks || 0,
        percentageScore: response.percentageScore || 0,
        questionScores: response.questionScores || {},
        questionsWithAnswers: response.questionsWithAnswers || [],
        evaluatedAt: response.evaluatedAt || new Date().toISOString(),
        completedAt: response.completedAt || response.createdAt,
        // Section-wise scores if available
        sectionScores: test.useDynamicSections && test.sections ? 
          test.sections.map((section, idx) => {
            let sectionScore = 0;
            (response.questionsWithAnswers || []).forEach((q, qIndex) => {
              const questionNum = qIndex + 1;
              if (questionNum >= section.startQuestion && questionNum <= section.endQuestion) {
                const questionId = q.id || `q${qIndex}`;
                sectionScore += response.questionScores?.[questionId] || 0;
              }
            });
            return {
              sectionId: section.id || idx,
              sectionName: section.name || `Section ${idx + 1}`,
              score: sectionScore
            };
          }) : []
      };

      const result = await saveStudentDocument(resultData);
      
      if (result.success) {
        toast.success(`Result sent to Batch Schedule for ${response.userName || response.userEmail}`);
        setSentResults(prev => new Set([...prev, response.id]));
      } else {
        toast.error(`Failed to send result: ${result.error}`);
      }
    } catch (error) {
      console.error('Error sending result to batch schedule:', error);
      toast.error(`Error sending result: ${error.message}`);
    } finally {
      setSendingResults(prev => ({ ...prev, [response.id]: false }));
    }
  };

  const handleSendAllToBatchSchedule = async () => {
    if (evaluatedResponses.length === 0) {
      toast.error('No evaluated results to send');
      return;
    }

    const unsentResponses = evaluatedResponses.filter(r => !sentResults.has(r.id));
    if (unsentResponses.length === 0) {
      toast.success('All results have already been sent');
      return;
    }

    setSendingResults(prev => {
      const newState = { ...prev };
      unsentResponses.forEach(r => {
        newState[r.id] = true;
      });
      return newState;
    });

    try {
      let successCount = 0;
      let failCount = 0;

      for (const response of unsentResponses) {
        if (!response.userEmail) {
          failCount++;
          continue;
        }

        try {
          const testDate = test.startTime || response.completedAt || response.createdAt || new Date().toISOString();
          
          const resultData = {
            studentEmail: response.userEmail.toLowerCase().trim(),
            studentId: response.userId || null,
            studentName: response.userName || response.userEmail || 'Unknown',
            documentType: 'result',
            documentUrl: '',
            examName: test.title || 'Test',
            examDate: testDate,
            batchId: test.batchId || null,
            coachingId: test.coachingId || null,
            testId: test.id,
            testResultId: response.id,
            score: response.score || 0,
            totalMarks: response.totalMarks || 0,
            percentageScore: response.percentageScore || 0,
            questionScores: response.questionScores || {},
            questionsWithAnswers: response.questionsWithAnswers || [],
            evaluatedAt: response.evaluatedAt || new Date().toISOString(),
            completedAt: response.completedAt || response.createdAt,
            sectionScores: test.useDynamicSections && test.sections ? 
              test.sections.map((section, idx) => {
                let sectionScore = 0;
                (response.questionsWithAnswers || []).forEach((q, qIndex) => {
                  const questionNum = qIndex + 1;
                  if (questionNum >= section.startQuestion && questionNum <= section.endQuestion) {
                    const questionId = q.id || `q${qIndex}`;
                    sectionScore += response.questionScores?.[questionId] || 0;
                  }
                });
                return {
                  sectionId: section.id || idx,
                  sectionName: section.name || `Section ${idx + 1}`,
                  score: sectionScore
                };
              }) : []
          };

          const result = await saveStudentDocument(resultData);
          
          if (result.success) {
            successCount++;
            setSentResults(prev => new Set([...prev, response.id]));
          } else {
            failCount++;
          }
        } catch (error) {
          failCount++;
          console.error(`Error sending result for ${response.userEmail}:`, error);
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully sent ${successCount} result(s) to Batch Schedule`);
      }
      if (failCount > 0) {
        toast.error(`Failed to send ${failCount} result(s)`);
      }
    } catch (error) {
      console.error('Error sending all results:', error);
      toast.error(`Error sending results: ${error.message}`);
    } finally {
      setSendingResults(prev => {
        const newState = { ...prev };
        unsentResponses.forEach(r => {
          newState[r.id] = false;
        });
        return newState;
      });
    }
  };

  if (!isOpen || !test) return null;

  const hasSections = test.useDynamicSections && test.sections && test.sections.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Test Evaluation: {test.title}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {testResponses.length} pending, {evaluatedResponses.length} evaluated
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700 flex">
          <button
            onClick={() => setActiveTab('pending')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'pending'
                ? 'border-b-2 border-purple-600 text-purple-600 dark:text-purple-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Pending Evaluation ({testResponses.length})
          </button>
          <button
            onClick={() => setActiveTab('evaluated')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'evaluated'
                ? 'border-b-2 border-green-600 text-green-600 dark:text-green-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Evaluated Results ({evaluatedResponses.length})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">Loading responses...</p>
            </div>
          ) : activeTab === 'pending' ? (
            /* Pending Evaluation Tab */
            <div className="space-y-4">
              {testResponses.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No responses pending evaluation
                  </p>
                </div>
              ) : (
                testResponses.map((response) => (
                  <div key={response.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          User: {response.userName || response.userEmail || 'Unknown'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Submitted: {new Date(response.completedAt || response.createdAt).toLocaleString()}
                        </p>
                      </div>
                      <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 text-xs rounded">
                        Pending Evaluation
                      </span>
                    </div>
                    
                    <div className="space-y-3">
                      {(response.questionsWithAnswers || []).map((qWithAnswer, qIndex) => {
                        const questionId = qWithAnswer.id || `q${qIndex}`;
                        const scoreKey = `${response.id}_${questionId}`;
                        return (
                          <div key={questionId} className="border-l-4 border-blue-500 pl-3 py-2 bg-gray-50 dark:bg-gray-800 rounded">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                                  Q{qIndex + 1}: {qWithAnswer.question?.substring(0, 100)}...
                                </p>
                                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                                  User Answer: {qWithAnswer.userAnswer !== null && qWithAnswer.userAnswer !== undefined 
                                    ? String(qWithAnswer.userAnswer) 
                                    : 'Not attempted'}
                                </p>
                              </div>
                              <div className="ml-4">
                                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                                  Score
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.5"
                                  value={evaluationScores[scoreKey] ?? ''}
                                  onChange={(e) => {
                                    setEvaluationScores(prev => ({
                                      ...prev,
                                      [scoreKey]: e.target.value ? parseFloat(e.target.value) : ''
                                    }));
                                  }}
                                  className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-white"
                                  placeholder="0"
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="mt-4 flex justify-end">
                      <button
                        onClick={() => handleSaveEvaluation(response)}
                        disabled={savingEvaluation}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {savingEvaluation ? 'Saving...' : 'Save Evaluation'}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            /* Evaluated Results Tab */
            <div>
              {evaluatedResponses.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No evaluated results yet
                  </p>
                </div>
              ) : (
                <div>
                  {/* Send All Button */}
                  <div className="mb-4 flex justify-end">
                    <button
                      onClick={handleSendAllToBatchSchedule}
                      disabled={evaluatedResponses.every(r => sentResults.has(r.id)) || Object.values(sendingResults).some(v => v)}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {Object.values(sendingResults).some(v => v) ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Send All to Batch Schedule
                        </>
                      )}
                    </button>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
                    <thead>
                      <tr className="bg-gray-100 dark:bg-gray-700">
                        <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-sm font-semibold text-gray-900 dark:text-white">
                          Rank
                        </th>
                        <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-sm font-semibold text-gray-900 dark:text-white">
                          Name
                        </th>
                        <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-sm font-semibold text-gray-900 dark:text-white">
                          Email
                        </th>
                        {hasSections && test.sections.map((section, idx) => (
                          <th key={section.id || idx} className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-center text-sm font-semibold text-gray-900 dark:text-white">
                            {section.name || `Section ${idx + 1}`}
                          </th>
                        ))}
                        <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-center text-sm font-semibold text-gray-900 dark:text-white">
                          Overall Score
                        </th>
                        <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-center text-sm font-semibold text-gray-900 dark:text-white">
                          Percentage
                        </th>
                        <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-center text-sm font-semibold text-gray-900 dark:text-white">
                          Evaluated At
                        </th>
                        <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-center text-sm font-semibold text-gray-900 dark:text-white">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {evaluatedResponses.map((response, index) => {
                        // Calculate section-wise scores only if sections exist
                        const sectionScores = {};
                        
                        if (hasSections) {
                          test.sections.forEach((section, sectionIdx) => {
                            let sectionScore = 0;
                            (response.questionsWithAnswers || []).forEach((q, qIndex) => {
                              const questionNum = qIndex + 1;
                              if (questionNum >= section.startQuestion && questionNum <= section.endQuestion) {
                                const questionId = q.id || `q${qIndex}`;
                                const score = response.questionScores?.[questionId] || 0;
                                sectionScore += score;
                              }
                            });
                            sectionScores[section.id || sectionIdx] = sectionScore;
                          });
                        }
                        
                        return (
                          <tr key={response.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm text-gray-900 dark:text-white font-medium">
                              {index + 1}
                            </td>
                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm text-gray-900 dark:text-white">
                              {response.userName || 'Unknown'}
                            </td>
                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
                              {response.userEmail || '-'}
                            </td>
                            {hasSections && test.sections.map((section, sectionIdx) => (
                              <td key={section.id || sectionIdx} className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-center text-sm text-gray-700 dark:text-gray-300">
                                {sectionScores[section.id || sectionIdx] || 0}
                              </td>
                            ))}
                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-center text-sm text-gray-900 dark:text-white font-semibold">
                              {response.score || 0} / {response.totalMarks || 0}
                            </td>
                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-center text-sm">
                              <span className={`font-medium ${
                                (response.percentageScore || 0) >= 90 ? 'text-green-600 dark:text-green-400' :
                                (response.percentageScore || 0) >= 70 ? 'text-blue-600 dark:text-blue-400' :
                                (response.percentageScore || 0) >= 50 ? 'text-yellow-600 dark:text-yellow-400' :
                                'text-red-600 dark:text-red-400'
                              }`}>
                                {response.percentageScore?.toFixed(2) || 0}%
                              </span>
                            </td>
                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-center text-xs text-gray-500 dark:text-gray-400">
                              {response.evaluatedAt ? new Date(response.evaluatedAt).toLocaleString() : '-'}
                            </td>
                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-center">
                              {sentResults.has(response.id) ? (
                                <div className="flex items-center justify-center gap-1 text-green-600 dark:text-green-400">
                                  <CheckCircle className="w-4 h-4" />
                                  <span className="text-xs">Sent</span>
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleSendToBatchSchedule(response)}
                                  disabled={sendingResults[response.id] || !response.userEmail}
                                  className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                  title="Send result to Batch Schedule"
                                >
                                  {sendingResults[response.id] ? (
                                    <>
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                      Sending...
                                    </>
                                  ) : (
                                    <>
                                      <Send className="w-3 h-3" />
                                      Send
                                    </>
                                  )}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestEvaluationModal;

